<template lang="pug">
#other-office-message
  .box
    .box-header
      .note
        span 連携済みの他事業所と、メッセージ・ファイルの送受信ができます。
    .box-body
      b-tabs(v-model="tabIndex" @activate-tab="activateTab" @changed="changedTab")
        //- list tab
        b-tab.tab
          template(v-slot:title)
            .d-flex.flex-row.align-items-center
              .tab-title
                span.tab-title-text.text-truncate.d-inline-block 事業所一覧
                span.badge(v-if="0 < unreadMessageCount") {{ unreadMessageCount }}
              span.mr-1(v-if="0 < unreadMessageCount" :style="getBadgeMargin(unreadMessageCount)")
          OtherOfficeConnectionListTab(ref="otherOfficeConnectionListTab" :items="items" :totalRows="totalRows"
            :isLoading="isLoading" @onDetail="addMessageTab")

        //- input tab
        b-tab(v-for="tab in tabs" :key="'tab-' + tab.no")
          template(v-slot:title)
            .message-reply-tab-title
              span.message-reply-tab-title-text.text-truncate.d-inline-block {{ tab.title }}
              button.close(type="button" @click="closeReplylTab") x
          OtherOfficeMessageListTab(ref="otherOfficeMessageListTab" :item="item" :messages="messages"
            @onUpdateMessage="confirmUpdateMessage"
            @onDeleteMessage="deleteMessage" @onUndoDeleteMessage="undoDeleteMessage"
            @onUpdateReply="confirmUpdateReply"
            @onDeleteReply="deleteReply" @onUndoDeleteReply="undoDeleteReply"
            @onRegisterMessage="confirmRegisterMessage" @onRegisterReplyMessage="confirmRegisterReplyMessage")

  //- loading modal
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
  //- register message modals
  OkCancelModal(v-model="showConfirmRegisterModal" title="新規トピックを送信します。よろしいですか？" @ok="registerMessage")
  OkModal(v-model="showRegisterSuccessModal" title="新規トピックの送信に成功しました。")
  //- update message modals
  OkCancelModal(v-model="showConfirmUpdateModal" title="トピックを更新します。よろしいですか？" @ok="updateMessage")
  OkModal(v-model="showUpdateSuccessModal" title="トピックの更新に成功しました。")
  //- register reply modals
  OkCancelModal(v-model="showConfirmRegisterReplyModal" title="トピックに返信します。よろしいですか？" @ok="registerReplyMessage")
  OkModal(v-model="showRegisterReplySuccessModal" title="返信に成功しました。")
  //- update reply modals
  OkCancelModal(v-model="showConfirmUpdateReplyModal" title="返信を更新します。よろしいですか？" @ok="updateReplyMessage")
  OkModal(v-model="showUpdateReplySuccessModal" title="返信の更新に成功しました。")
  //- confrict modal
  OkModal(v-model="showConfrictModal" :title="confrictModalTitle" @ok="refreshMessages")
  //- message and reply progress modal
  ProgressLoading(v-model="currentUploadCount" :max="uploadCount" :isBusy="progressLoading" :message="progressMessage")

  //- file upload error modal
  FileUploadErrorModal(v-model="showUploadErrorModal")
</template>

<script lang="ts">
import { Component, Inject, Ref, Vue } from "vue-property-decorator";
import { BTab } from "bootstrap-vue";
import { userStore } from "@/store/store";

import SystemError from "@/commons/models/SystemError";

import OtherOfficeConnectionsService, { OtherOfficeConnectionGetModel } from "@/commons/services/OtherOfficeConnections";
import OtherOfficeMessagesService, {
  OtherOfficeMessageGetModel,
  OtherOfficeMessagePostModel,
  FileUrl,
  OtherOfficeMessagePatchModel,
  UpdateType
} from "@/commons/services/OtherOfficeMessages";
import OtherOfficeReplyMessagesService, {
  OtherOfficeReplyMessageGetModel,
  OtherOfficeReplyMessagePatchModel,
  OtherOfficeReplyMessagePostModel,
  UpdateType as ReplyUpdateType
} from "@/commons/services/OtherOfficeReplyMessages";

import S3LocationsService from "@/commons/services/S3Locations";
import { S3LocationRequest, S3LocationResponse, S3LocationType } from "@/commons/models/S3Location";
import S3UploadsService from "@/commons/services/S3Uploads";

import OtherOfficeConnectionListTab from "@/pc/containers/sendOtherOfficeMessages/OtherOfficeConnectionListTab.vue";
import OtherOfficeMessageListTab, {
  OtherOfficeMessageListTabMessage,
  OtherOfficeMessageListTabMessageReply
} from "@/pc/containers/sendOtherOfficeMessages/OtherOfficeMessageListTab.vue";
import { OtherOfficeConnectionTableItem } from "@/pc/containers/sendOtherOfficeMessages/OtherOfficeConnectionTable.vue";

import FileUploadErrorModal from "@/pc/basics/FileUploadErrorModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ProgressLoading from "@/pc/basics/ProgressLoading.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import { PcUrl } from "@/commons/constants/PcUrl";

/**
 * ### 他事業所メッセージ
 *
 */
@Component({
  components: {
    FileUploadErrorModal,
    OtherOfficeConnectionListTab,
    OtherOfficeMessageListTab,
    PageLoading,
    ProgressLoading,
    OkCancelModal,
    OkModal
  }
})
export default class OtherOfficeMessages extends Vue {
  /** 他事業所連携管理サービス */
  @Inject("OtherOfficeConnectionsService") otherOfficeConnectionsService!: OtherOfficeConnectionsService;
  /** 他事業所メッセージサービス */
  @Inject("OtherOfficeMessagesService") otherOfficeMessagesService!: OtherOfficeMessagesService;
  /** 他事業所返信メッセージサービス */
  @Inject("OtherOfficeReplyMessagesService") otherOfficeReplyMessagesService!: OtherOfficeReplyMessagesService;

  /** S3格納先URLサービス */
  @Inject("S3LocationsService") s3LocationsService!: S3LocationsService;
  /** S3ファイルアップロードサービス */
  @Inject("S3UploadsService") s3UploadsService!: S3UploadsService;

  /** 他事業所連携管理リストタブ */
  @Ref()
  otherOfficeConnectionListTab!: OtherOfficeConnectionListTab;
  /** 他事業所メッセージ詳細タブ */
  @Ref()
  otherOfficeMessageListTab!: Array<OtherOfficeMessageListTab>;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** タブインデックス */
  tabIndex: number = 0;
  /** メッセージ詳細タブ */
  tabs: Array<any> = [];
  /** 詳細タブからの戻り先 */
  backTabIndex: number = 0;
  /** 詳細タブ閉じるボタンが押下されたか */
  onCloseButton: boolean = false;

  /** 未読メッセージ数 */
  unreadMessageCount: number = 0;

  /** テーブル読込み中 */
  isLoading: boolean = true;
  /** テーブル行 */
  items: Array<OtherOfficeConnectionTableItem> = [];
  /** 全体行数 */
  totalRows: number = 0;

  /** メッセージ表示対象の他事業所連携 */
  item: OtherOfficeConnectionTableItem | null = null;
  /** 他事業所連携メッセージ */
  messages: Array<OtherOfficeMessageListTabMessage> = [];

  /** メッセージ新規投稿内容 */
  registerMessagePayload: { requestBody: OtherOfficeMessagePostModel; files: File[] } | null = null;
  /** メッセージ送信確認モーダル表示可否 */
  showConfirmRegisterModal: boolean = false;
  /** メッセージ送信完了モーダル表示可否 */
  showRegisterSuccessModal: boolean = false;

  /** メッセージ更新内容 */
  updateMessagePayload: { message: OtherOfficeMessageListTabMessage; requestBody: OtherOfficeMessagePatchModel; files: Array<File>; index: number } | null = null;
  /** メッセージ更新確認モーダル表示可否 */
  showConfirmUpdateModal: boolean = false;
  /** メッセージ更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** メッセージ返信内容 */
  registerReplyMessagePayload: { requestBody: OtherOfficeReplyMessagePostModel; files: File[]; datetime: number; index: number } | null = null;
  /** メッセージ返信送信確認モーダル表示可否 */
  showConfirmRegisterReplyModal: boolean = false;
  /** メッセージ返信送信完了モーダル表示可否 */
  showRegisterReplySuccessModal: boolean = false;

  /** メッセージ更新内容 */
  updateReplyMessagePayload: {
    message: OtherOfficeMessageListTabMessage;
    reply: OtherOfficeMessageListTabMessageReply;
    requestBody: OtherOfficeReplyMessagePatchModel;
    files: Array<File>;
    index: number;
    replyIndex: number;
  } | null = null;
  /** メッセージ更新確認モーダル表示可否 */
  showConfirmUpdateReplyModal: boolean = false;
  /** メッセージ更新完了モーダル表示可否 */
  showUpdateReplySuccessModal: boolean = false;

  /** 現在のアップロード数 */
  currentUploadCount: number = 0;
  /** アップロード数最大値 */
  uploadCount: number = 0;
  /** プログレス付きローディング表示可否 */
  progressLoading: boolean = false;
  /** プログレス付きローディングメッセージ */
  progressMessage: string = "";

  /** 競合通知モーダル表示可否 */
  showConfrictModal: boolean = false;
  /** 競合通知モーダルタイトル */
  confrictModalTitle: string = "";

  /** ファイルアップロードエラーモーダル表示可否 */
  showUploadErrorModal: boolean = false;

  /**
   * vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面情報を作成する
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    const connections: Array<OtherOfficeConnectionGetModel> = await this.getOtherOfficeConnections();
    const items: Array<OtherOfficeConnectionTableItem> = this.createItems(connections);
    this.items = items;
    this.totalRows = items.length;

    // 非同期で未読が存在するかチェックする
    this.setUnreads();

    this.isLoading = false;
    return;
  }

  /**
   * ### 他事業所連携管理を取得する
   */
  public async getOtherOfficeConnections(): Promise<Array<OtherOfficeConnectionGetModel>> {
    const response: Array<OtherOfficeConnectionGetModel> = await this.otherOfficeConnectionsService.gets(userStore);
    return response;
  }

  /**
   * ### 他事業所連携管理テーブルの行を作成する
   */
  public createItems(connections: Array<OtherOfficeConnectionGetModel>): Array<OtherOfficeConnectionTableItem> {
    const items: Array<OtherOfficeConnectionTableItem> = connections.filter((connection) => {
      if (connection.registerd === true && connection.registerdFacilityId != null) {
        return true;
      } else {
        return false;
      }
    });
    for (const item of items) {
      item.unreadCount = undefined;
    }
    return items;
  }

  public async setUnreads(): Promise<void> {
    try {
      for (const item of this.items) {
        const messages: Array<OtherOfficeMessageListTabMessage> = await this.getOtherOfficeMessageListTabMessage(item.registerdFacilityId);

        // 非同期処理中に画面遷移していた場合、このコンポーネントvueインスタンスが無くなっている可能性がある
        // URLを見て判断する
        if (this.$route.fullPath == PcUrl.OtherOfficeMessages) {
          let unreadCount: number = 0;
          for (const message of messages) {
            if (
              message.unreadFacilities.find((id) => {
                if (userStore.loginUser.facilityId === id) {
                  return true;
                } else {
                  return false;
                }
              })
            ) {
              unreadCount++;
            }
          }
          item.unreadCount = unreadCount;
          this.$forceUpdate();
        }
      }
    } catch (error) {
      // 失敗してもコンソールにエラーを表示するだけでシステムエラーにはしない
      console.error("sendOtherOfficeMessages.vue setUnreads() error", error);
    }
    return;
  }

  /**
   * ### 未読数バッジはabsoluteで表示しているので、左マージンを計算して返す
   */
  public getBadgeMargin(count: number): string {
    // 10件未満
    if (count < 10) {
      return "margin-left: 24px;";
    }
    // 10件以上
    else if (10 <= count) {
      return "margin-left: 32px;";
    }
    // その他
    else {
      return "";
    }
  }

  /**
   * ### 他事業所メッセージタブを追加する
   */
  public async addMessageTab(item: OtherOfficeConnectionTableItem): Promise<void> {
    this.addTab(item);
    this.$nextTick(async () => {
      this.isBusy = true;
      this.loadingMessage = "読込み中...";

      this.item = item;
      const messages: Array<OtherOfficeMessageListTabMessage> = await this.getOtherOfficeMessageListTabMessage(item.registerdFacilityId);
      this.messages = messages;

      // 取得したもので未読のものがあれば非同期で既読に更新する
      for (const message of messages) {
        if (0 <= message.unreadFacilities.indexOf(userStore.loginUser.facilityId)) {
          this.updateReadMessage(message);
        }
        // 未読数を0にする
        this.item.unreadCount = 0;
      }

      // スクロールを最下段にするため、詳細タブの描画完了を待ってからスクロール位置を移動させる
      this.$nextTick(() => {
        this.otherOfficeMessageListTab[0].bottomScroll();
      });

      this.isBusy = false;
    });
    return;
  }

  /**
   * ### メッセージ詳細タブを追加する
   */
  public addTab(item: OtherOfficeConnectionTableItem): void {
    this.tabs.splice(0, 1);
    this.tabs.push({
      no: 0,
      title: `${item.officeName}とのメッセージ`
    });
    this.backTabIndex = 0;
    return;
  }

  /**
   * ### 他事業所メッセージ（投稿）を取得する
   */
  public async getOtherOfficeMessageListTabMessage(otherOfficeFacilityId: number): Promise<Array<OtherOfficeMessageListTabMessage>> {
    let messages: Array<OtherOfficeMessageGetModel> = [];

    try {
      messages = await this.otherOfficeMessagesService.gets(userStore, otherOfficeFacilityId);
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemError.statusCode == 413) {
        const count: number = await this.otherOfficeMessagesService.getCount(userStore, otherOfficeFacilityId);
        const loopEnd: number = Math.ceil(count / 300);

        for (let i = 0; i < loopEnd; i++) {
          const params: { startIndex: number; endIndex: number } = {
            startIndex: i * 300,
            endIndex: i * 300 + 300
          };
          messages = messages.concat(await this.otherOfficeMessagesService.gets(userStore, otherOfficeFacilityId, params));
        }
      } else {
        throw error;
      }
    }

    // 取得した情報から実際に画面で使用する情報を作成する
    const tabMessages: Array<OtherOfficeMessageListTabMessage> = [];
    for (const message of messages) {
      tabMessages.push({
        ...message,
        // 編集中か否か
        isEditing: false,
        // 返信を表示するか否か。初期値はfalse
        showReplies: false,
        // 取得うした返信。この時点では未取得なので空配列
        replies: [],
        // 登録する返信情報
        inputReply: {
          text: "",
          files: []
        }
      });
    }
    return tabMessages;
  }

  /**
   * ### 他事業所メッセージ（投稿）を既読に更新する
   */
  public async updateReadMessage(message: OtherOfficeMessageListTabMessage): Promise<void> {
    try {
      const requestBody: OtherOfficeMessagePatchModel = {
        updateType: UpdateType.READ,
        title: undefined,
        text: undefined,
        fileUrls: undefined,
        updated: message.updated
      };
      await this.otherOfficeMessagesService.patch(userStore, this.item!.registerdFacilityId, message.datetime, requestBody);
    } catch (error) {
      // 非同期処理なのでエラーが出ても無視する
      console.error("OtherOfficeMessages.vue updateReadMessage()", error);
    }
    return;
  }

  /**
   * ### メッセージ詳細表示用タブを消す
   */
  public closeReplylTab(): void {
    this.tabs.splice(0, 1);
    this.onCloseButton = true;
    return;
  }

  /**
   * ### タブの切り替えイベント
   *
   * - メッセージタブから他のタブへ移動した場合、メッセージタブを削除する
   */
  public activateTab(newTabIndex: number, prevTabIndex: number): void {
    // タブ位置が返信以外であればメッセージタブを削除する
    if (newTabIndex != 1 && prevTabIndex == 1) {
      this.tabs.splice(0, 1);
    }
    return;
  }

  /**
   * ### タブの追加・削除イベント
   */
  public changedTab(currentTabs: Array<BTab>): void {
    // メッセージタブが追加された場合
    if (1 < currentTabs.length && currentTabs[1]) {
      currentTabs[1].activate();
    }
    // メッセージタブが消された場合
    if (this.onCloseButton == true && 1 == currentTabs.length && currentTabs[this.backTabIndex]) {
      currentTabs[this.backTabIndex].activate();
      this.onCloseButton = false;
    }
    return;
  }

  /**
   * ### 投稿更新確認モーダルを表示する
   */
  public confirmUpdateMessage(payload: { message: OtherOfficeMessageListTabMessage; requestBody: OtherOfficeMessagePatchModel; files: Array<File>; index: number }): void {
    this.updateMessagePayload = payload;
    this.showConfirmUpdateModal = true;
    return;
  }

  /**
   * ### 投稿を更新する
   */
  public async updateMessage(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const payload = this.updateMessagePayload as { message: OtherOfficeMessageListTabMessage; requestBody: OtherOfficeMessagePatchModel; files: File[]; index: number };

      // ファイルアップロードするタイミングでファイルが削除（移動含む）、リネームされた場合は失敗するのでここでtry-catchする
      // アップロードに失敗した場合は添付されたファイルを全てリセットしてモーダルで再添付を促す
      try {
        const fileUrls: Array<FileUrl> = await this.createFileUrls(payload.files);
        payload.requestBody.fileUrls = fileUrls;
      } catch (error) {
        this.isBusy = false;

        this.showUploadErrorModal = true;
        this.otherOfficeMessageListTab[0].resetUpdateTopicFiles(payload.index);

        return;
      }

      const updated = await this.otherOfficeMessagesService.patch(userStore, this.item!.registerdFacilityId, payload.message.datetime, payload.requestBody);

      // 更新したメッセージを更新する
      for (const message of this.messages) {
        if (message.datetime == updated.datetime) {
          message.isEditing = !message.isEditing;
          message.latestReplyDatetime = updated.latestReplyDatetime;
          message.title = updated.title;
          message.text = updated.text;
          message.fileUrls = updated.fileUrls;
          message.updated = updated.updated;
        }
      }
      // 最終送信日時が更新されるのでソートし直す
      this.messages = this.sortMessages(this.messages);
      // スクロールを最下段にするため、詳細タブの描画完了を待ってからスクロール位置を移動させる
      this.$nextTick(() => {
        this.otherOfficeMessageListTab[0].bottomScroll();
      });

      this.showUpdateSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        this.confrictModalTitle = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
        this.showConfrictModal = true;
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    this.isBusy = false;

    return;
  }

  /**
   * ### メッセージをソートする
   */
  public sortMessages(messages: Array<OtherOfficeMessageListTabMessage>): Array<OtherOfficeMessageListTabMessage> {
    const sortedMessages: Array<OtherOfficeMessageListTabMessage> = messages.concat().sort((a, b) => {
      if (a.latestReplyDatetime < b.latestReplyDatetime) {
        return -1;
      }
      if (b.latestReplyDatetime < a.latestReplyDatetime) {
        return 1;
      }
      return 0;
    });
    return sortedMessages;
  }

  /**
   * ### メッセージの削除
   */
  public async deleteMessage(deleteTargetMessage: OtherOfficeMessageListTabMessage): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    const deleted = await this.otherOfficeMessagesService.delete(userStore, this.item!.registerdFacilityId, deleteTargetMessage.datetime);

    // 削除したメッセージのdeleteFlagを更新する
    for (const message of this.messages) {
      if (message.datetime == deleteTargetMessage.datetime) {
        message.deleteFlag = deleted.deleteFlag;
        message.updated = deleted.updated;
      }
    }

    this.isBusy = false;
    return;
  }

  /**
   * ### メッセージ削除を元に戻す
   */
  public async undoDeleteMessage(undoDeleteTargetMessage: OtherOfficeMessageListTabMessage): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const requestBody: OtherOfficeMessagePatchModel = this.createUndoDeleteMessageRequestBody(undoDeleteTargetMessage);
      const updated = await this.otherOfficeMessagesService.patch(userStore, this.item!.registerdFacilityId, undoDeleteTargetMessage.datetime, requestBody);

      // 削除したメッセージのdeleteFlagを更新する
      for (const message of this.messages) {
        if (message.datetime == undoDeleteTargetMessage.datetime) {
          message.deleteFlag = updated.deleteFlag;
          message.updated = updated.updated;
        }
      }
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        this.confrictModalTitle = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
        this.showConfrictModal = true;
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  public createUndoDeleteMessageRequestBody(undoDeleteTargetMessage: OtherOfficeMessageListTabMessage): OtherOfficeMessagePatchModel {
    return {
      updateType: UpdateType.UNDO,
      title: undefined,
      text: undefined,
      fileUrls: undefined,
      updated: undoDeleteTargetMessage.updated
    };
  }

  /**
   * ### 詳細タブのメッセージの再取得
   */
  public async refreshMessages(): Promise<void> {
    this.closeReplylTab();
    await this.addMessageTab(this.item!);
    return;
  }

  /**
   * ### 返信メッセージ更新確認モーダルを表示する
   */
  public confirmUpdateReply(payload: {
    message: OtherOfficeMessageListTabMessage;
    reply: OtherOfficeMessageListTabMessageReply;
    requestBody: OtherOfficeReplyMessagePatchModel;
    files: Array<File>;
    index: number;
    replyIndex: number;
  }): void {
    this.updateReplyMessagePayload = payload;
    this.showConfirmUpdateReplyModal = true;
    return;
  }

  /**
   * ### 返信メッセージを更新する
   */
  public async updateReplyMessage(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const payload = this.updateReplyMessagePayload as {
        message: OtherOfficeMessageListTabMessage;
        reply: OtherOfficeMessageListTabMessageReply;
        requestBody: OtherOfficeReplyMessagePatchModel;
        files: Array<File>;
        index: number;
        replyIndex: number;
      };
      // ファイルアップロードするタイミングでファイルが削除（移動含む）、リネームされた場合は失敗するのでここでtry-catchする
      // アップロードに失敗した場合は添付されたファイルを全てリセットしてモーダルで再添付を促す
      try {
        const fileUrls: Array<FileUrl> = await this.createFileUrls(payload.files);
        payload.requestBody.fileUrls = fileUrls;
      } catch (error) {
        this.isBusy = false;

        this.showUploadErrorModal = true;
        this.otherOfficeMessageListTab[0].resetUpdateReplyFiles(payload.index, payload.replyIndex);

        return;
      }

      const updated = await this.otherOfficeReplyMessagesService.patch(
        userStore,
        this.item!.registerdFacilityId,
        payload.message.datetime,
        payload.reply.datetime,
        payload.requestBody
      );

      // 更新した返信メッセージを更新する
      for (const message of this.messages) {
        if (message.datetime == payload.message.datetime) {
          message.latestReplyDatetime = updated.updated;
          message.updated = updated.updated;

          for (const reply of message.replies) {
            if (reply.datetime == payload.reply.datetime) {
              reply.isEditing = !reply.isEditing;
              reply.text = updated.text;
              reply.fileUrls = updated.fileUrls;
              reply.updated = updated.updated;
            }
          }
        }
      }
      // 最終送信日時が更新されるのでソートし直す
      this.messages = this.sortMessages(this.messages);
      // スクロールを最下段にするため、詳細タブの描画完了を待ってからスクロール位置を移動させる
      this.$nextTick(() => {
        this.otherOfficeMessageListTab[0].bottomScroll();
      });

      this.showUpdateReplySuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        this.confrictModalTitle = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
        this.showConfrictModal = true;
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 返信メッセージの削除
   */
  public async deleteReply(payload: { deleteTargetMessage: OtherOfficeMessageListTabMessage; reply: OtherOfficeReplyMessageGetModel }): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    const deleted = await this.otherOfficeReplyMessagesService.delete(userStore, this.item!.registerdFacilityId, payload.deleteTargetMessage.datetime, payload.reply.datetime);

    // 削除したメッセージのdeleteFlagを更新する
    for (const message of this.messages) {
      if (message.datetime == payload.deleteTargetMessage.datetime) {
        for (const reply of message.replies) {
          if (reply.datetime == payload.reply.datetime) {
            reply.deleteFlag = deleted.deleteFlag;
            reply.updated = deleted.updated;
          }
        }
      }
    }

    this.isBusy = false;
    return;
  }

  /**
   * ### 返信メッセージ削除を元に戻す
   */
  public async undoDeleteReply(payload: { undoDeleteTargetMessage: OtherOfficeMessageListTabMessage; reply: OtherOfficeReplyMessageGetModel }): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const requestBody: OtherOfficeReplyMessagePatchModel = this.createUndoDeleteReplyRequestBody(payload.reply);
      const updated = await this.otherOfficeReplyMessagesService.patch(
        userStore,
        this.item!.registerdFacilityId,
        payload.undoDeleteTargetMessage.datetime,
        payload.reply.datetime,
        requestBody
      );

      // 削除したメッセージのdeleteFlagを更新する
      for (const message of this.messages) {
        if (message.datetime == payload.undoDeleteTargetMessage.datetime) {
          for (const reply of message.replies) {
            if (reply.datetime == payload.reply.datetime) {
              reply.deleteFlag = updated.deleteFlag;
              reply.updated = updated.updated;
            }
          }
        }
      }
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        this.confrictModalTitle = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
        this.showConfrictModal = true;
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  public createUndoDeleteReplyRequestBody(reply: OtherOfficeReplyMessageGetModel): OtherOfficeReplyMessagePatchModel {
    return {
      updateType: ReplyUpdateType.UNDO,
      text: undefined,
      fileUrls: undefined,
      updated: reply.updated
    };
  }

  /**
   * ### 新規投稿確認モーダルを表示する
   */
  public confirmRegisterMessage(payload: { requestBody: OtherOfficeMessagePostModel; files: File[] }): void {
    this.registerMessagePayload = payload;
    this.showConfirmRegisterModal = true;
    return;
  }

  /**
   * ### 新規投稿を登録する
   *
   * - ファイルのアップロード
   * - ファイルのアップロード結果を追加してAPIリクエスト
   * - 詳細タブの入力内容をリセットさせる
   * - 作成した情報を詳細タブに反映させる
   */
  public async registerMessage(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "登録中...";

    const payload = this.registerMessagePayload as { requestBody: OtherOfficeMessagePostModel; files: File[] };

    // ファイルアップロードするタイミングでファイルが削除（移動含む）、リネームされた場合は失敗するのでここでtry-catchする
    // アップロードに失敗した場合は添付されたファイルを全てリセットしてモーダルで再添付を促す
    try {
      const fileUrls: Array<FileUrl> = await this.createFileUrls(payload.files);
      payload.requestBody.fileUrls = fileUrls;
    } catch (error) {
      this.isBusy = false;

      this.showUploadErrorModal = true;
      this.otherOfficeMessageListTab[0].resetRegisterTopicFiles();

      return;
    }

    const registered = await this.otherOfficeMessagesService.post(userStore, this.item!.registerdFacilityId, payload.requestBody);

    if (Array.isArray(this.otherOfficeMessageListTab)) {
      this.otherOfficeMessageListTab[0].initialize();
    }
    this.messages.push({
      ...registered,
      // 編集中か否か
      isEditing: false,
      // 日付に関連するものはBIGINTにされてしまっているので変換する
      datetime: registered.datetime,
      latestReplyDatetime: registered.latestReplyDatetime,
      created: registered.created,
      updated: registered.updated,
      // 返信を表示するか否か。初期値はfalse
      showReplies: false,
      // 取得うした返信。この時点では未取得なので空配列
      replies: [],
      // 返信情報
      inputReply: {
        text: "",
        files: []
      }
    });
    // スクロールを最下段にするため、詳細タブの描画完了を待ってからスクロール位置を移動させる
    this.$nextTick(() => {
      this.otherOfficeMessageListTab[0].bottomScroll();
    });

    this.isBusy = false;
    this.showRegisterSuccessModal = true;

    return;
  }

  /**
   * ### ファイルアップロードし、S3URL情報を作成する
   */
  public async createFileUrls(files: File[]): Promise<Array<FileUrl>> {
    if (files.length < 1) {
      return [];
    }
    const urls: Array<S3LocationResponse> = await this.getUploadUrls(files, S3LocationType.OTHER_OFFICE_MESSAGES);
    const fileUrls: Array<FileUrl> = await this.uploadFiles(files, urls);
    return fileUrls;
  }

  /**
   * ### ファイルアップロード先のURLを発行する
   */
  public async getUploadUrls(files: Array<File>, s3LocationType: S3LocationType): Promise<Array<S3LocationResponse>> {
    const extensions: Array<string> = [];
    for (const file of files) {
      const _file: Array<string> = file.name.split("/");
      const _extensions: Array<string> = _file[_file.length - 1].split(".");
      extensions.push("." + _extensions[_extensions.length - 1]);
    }
    const requestBody: S3LocationRequest = {
      type: s3LocationType,
      count: files.length,
      extensions: extensions,
      params: {
        userId: userStore.loginUser.userId
      },
      acl: "public-read"
    };
    const urls: Array<S3LocationResponse> = await this.s3LocationsService.post(userStore, requestBody);

    return urls;
  }

  /**
   * ### ファイルをアップロードする
   *
   * - プログレスモーダルを表示する
   * - ファイルをS3にアップロードする
   */
  public async uploadFiles(files: File[], urls: Array<S3LocationResponse>): Promise<Array<FileUrl>> {
    this.currentUploadCount = 0;
    this.uploadCount = files.length;
    this.progressMessage = "ファイルアップロード中。\n完了するまでそのままお待ちください。\n完了前に別画面に遷移しますと処理がキャンセルされます。";
    this.progressLoading = true;

    const fileUrls: Array<FileUrl> = [];
    try {
      for (let i = 0; i < urls.length; i++) {
        this.currentUploadCount = i;
        await this.s3UploadsService.uploadFile(urls[i], files[i]);

        const preUrl: string = urls[i].url.endsWith("/") ? urls[i].url : `${urls[i].url}/`;
        const s3Url: string = `${preUrl}${urls[i].fields["key"]}`;
        fileUrls.push({
          name: files[i].name,
          url: s3Url
        });
      }
    } finally {
      this.progressLoading = false;
    }

    return fileUrls;
  }

  /**
   * ### 投稿への返信確認モーダルを表示する
   */
  public confirmRegisterReplyMessage(payload: { requestBody: OtherOfficeReplyMessagePostModel; files: File[]; datetime: number; index: number }): void {
    this.registerReplyMessagePayload = payload;
    this.showConfirmRegisterReplyModal = true;
    return;
  }

  public async registerReplyMessage(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "登録中...";

    const payload = this.registerReplyMessagePayload as { requestBody: OtherOfficeReplyMessagePostModel; files: File[]; datetime: number; index: number };

    // ファイルアップロードするタイミングでファイルが削除（移動含む）、リネームされた場合は失敗するのでここでtry-catchする
    // アップロードに失敗した場合は添付されたファイルを全てリセットしてモーダルで再添付を促す
    try {
      const fileUrls: Array<FileUrl> = await this.createFileUrls(payload.files);
      payload.requestBody.fileUrls = fileUrls;
    } catch (error) {
      this.isBusy = false;

      this.showUploadErrorModal = true;
      this.otherOfficeMessageListTab[0].resetRegisterReplyFiles(payload.index);

      return;
    }

    const registered = await this.otherOfficeReplyMessagesService.post(userStore, this.item!.registerdFacilityId, payload.datetime, payload.requestBody);

    // 返信を投稿に追加し、返信内容を初期化する
    for (const message of this.messages) {
      if (message.datetime == payload.datetime) {
        message.latestReplyDatetime = registered.datetime;
        message.replyCount++;

        message.replies.push({
          otherOfficeMessageKey: registered.otherOfficeMessageKey,
          datetime: registered.datetime,
          senderFacilityId: registered.senderFacilityId,
          text: registered.text,
          fileUrls: registered.fileUrls,
          // 編集中か否か
          isEditing: false,
          deleteFlag: registered.deleteFlag,
          created: registered.created,
          updated: registered.updated,
          userId: ""
        });

        message.inputReply.text = "";
        message.inputReply.files = [];
      }
    }
    // 最終送信日時が更新されるのでソートし直す
    this.messages = this.sortMessages(this.messages);
    // スクロールを最下段にするため、詳細タブの描画完了を待ってからスクロール位置を移動させる
    this.$nextTick(() => {
      this.otherOfficeMessageListTab[0].bottomScroll();
    });

    this.isBusy = false;
    this.showRegisterReplySuccessModal = true;
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

#other-office-message {
  max-width: 1292px;
  margin-left: auto;
  margin-right: auto;

  .box-body {
    .tab-title {
      position: relative;
      max-height: 23px;
    }

    .badge {
      position: absolute;
      top: 1px;
      z-index: 1001;
      margin: 0 0 0 4px !important;
      min-width: 10px;
      padding: 3px 7px;
      font-size: 14px;
      font-weight: bold;
      color: #fff;
      line-height: 1;
      vertical-align: middle;
      white-space: nowrap;
      text-align: center;
      background-color: $notification-badge-color;
      border-radius: 10px;
    }

    .spinner {
      width: 16px;
      height: 16px;
      color: $loading;
    }
    .refresh {
      color: $font-base-color;
      opacity: 0.5;
      text-shadow: 0 1px 0 #fff;
      cursor: pointer;
    }
    .refresh:hover {
      opacity: 0.75;
    }

    .message-reply-tab-title {
      max-height: 23px;

      .message-reply-tab-title-text {
        max-width: 220px;
        margin-right: 12px;
      }
    }
  }
}
</style>
